import { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreateSecurityProduct, useUpdateSecurityProduct } from "@/api/devices";
import type { WindowsSecurityProduct } from "@/api/types";

const CATEGORIES = ["FIREWALL", "ANTIVIRUS", "ANTISPYWARE"] as const;
const STATES = ["ON", "OFF", "UNKNOWN"] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  deviceId: string;
  product?: WindowsSecurityProduct | null;
}

export function SecurityProductModal({ open, onClose, tenantId, deviceId, product }: Props) {
  const isEdit = !!product;
  const create = useCreateSecurityProduct(tenantId, deviceId);
  const update = useUpdateSecurityProduct(tenantId, deviceId);
  const isPending = create.isPending || update.isPending;

  const [name, setName] = useState("");
  const [category, setCategory] = useState<WindowsSecurityProduct["category"]>("ANTIVIRUS");
  const [state, setState] = useState<WindowsSecurityProduct["state"]>("ON");
  const [stateTimestamp, setStateTimestamp] = useState("");
  const [signaturesUpToDate, setSignaturesUpToDate] = useState(false);

  useEffect(() => {
    if (open) {
      setName(product?.name ?? "");
      setCategory(product?.category ?? "ANTIVIRUS");
      setState(product?.state ?? "ON");
      setStateTimestamp(product?.state_timestamp ? product.state_timestamp.slice(0, 16) : new Date().toISOString().slice(0, 16));
      setSignaturesUpToDate(product?.signatures_up_to_date ?? false);
    }
  }, [open, product]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name,
      category,
      state,
      state_timestamp: new Date(stateTimestamp).toISOString(),
      signatures_up_to_date: signaturesUpToDate,
    };
    const onSuccess = { onSuccess: onClose };
    if (isEdit) {
      update.mutate({ id: product.id, payload }, onSuccess);
    } else {
      create.mutate({ payload }, onSuccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit" : "Add"} Security Product</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Name *</label>
          <Input value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Category *</label>
          <Select value={category} onChange={e => setCategory(e.target.value as WindowsSecurityProduct["category"])}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>)}
          </Select>
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">State *</label>
          <Select value={state} onChange={e => setState(e.target.value as WindowsSecurityProduct["state"])}>
            {STATES.map(s => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
          </Select>
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">State Timestamp *</label>
          <Input type="datetime-local" value={stateTimestamp} onChange={e => setStateTimestamp(e.target.value)} required />
        </div>
        <div className="flex items-center justify-between">
          <label className="text-sm text-text-muted">Signatures Up to Date</label>
          <Switch checked={signaturesUpToDate} onChange={setSignaturesUpToDate} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button type="submit" disabled={isPending || !name}>{isPending ? "Saving..." : isEdit ? "Update" : "Add"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
